import React, { useState } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import './StatusChangeModal.css';

interface StatusOption {
  value: string;
  label: string;
  variant: 'success' | 'warning' | 'danger' | 'info';
  description?: string;
}

interface StatusChangeModalProps {
  show: boolean;
  onHide: () => void;
  onConfirm: (newStatus: string) => void;
  currentStatus: string;
  itemName: string;
  itemType?: string;
  loading?: boolean;
  statusOptions?: StatusOption[];
}

const defaultStatusOptions: StatusOption[] = [
  {
    value: 'active',
    label: 'Active',
    variant: 'success',
    description: 'Item is live and visible to users'
  },
  {
    value: 'deleted',
    label: 'Inactive',
    variant: 'danger',
    description: 'Item is hidden from users and inactive'
  }
];

const StatusChangeModal: React.FC<StatusChangeModalProps> = ({
  show,
  onHide,
  onConfirm,
  currentStatus,
  itemName,
  itemType = 'item',
  loading = false,
  statusOptions = defaultStatusOptions
}) => {
  const [selectedStatus, setSelectedStatus] = useState<string>(currentStatus);

  const handleConfirm = () => {
    if (selectedStatus !== currentStatus) {
      onConfirm(selectedStatus);
    } else {
      onHide();
    }
  };

  const getStatusVariant = (status: string): string => {
    const option = statusOptions.find(opt => opt.value === status);
    return option?.variant || 'secondary';
  };

  const getStatusLabel = (status: string): string => {
    const option = statusOptions.find(opt => opt.value === status);
    return option?.label || status.charAt(0).toUpperCase() + status.slice(1);
  };

  const selectedOption = statusOptions.find(opt => opt.value === selectedStatus);
  const hasChanges = selectedStatus !== currentStatus;

  return (
    <Modal show={show} onHide={onHide} centered size="sm">
      <Modal.Header closeButton className="status-modal-header">
        <Modal.Title className="status-modal-title">
          <i className="fas fa-toggle-on me-2 text-primary"></i>
          Change Status
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body className="status-modal-body">
        <div className="current-item-info">
          <h6 className="item-name">{itemName}</h6>
          <div className="current-status">
            <span className="text-muted">Current status: </span>
            <span className={`badge bg-${getStatusVariant(currentStatus)}`}>
              {getStatusLabel(currentStatus)}
            </span>
          </div>
        </div>

        <Form.Group className="mt-4">
          <Form.Label className="fw-semibold">Select new status:</Form.Label>
          {statusOptions.map((option) => (
            <Form.Check
              key={option.value}
              type="radio"
              id={`status-${option.value}`}
              name="status"
              value={option.value}
              checked={selectedStatus === option.value}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="status-option"
              label={
                <div className="status-option-content">
                  <div className="d-flex align-items-center">
                    <span className={`badge bg-${option.variant} me-2`}>
                      {option.label}
                    </span>
                    <span className="option-label">{option.label}</span>
                  </div>
                  {option.description && (
                    <small className="text-muted option-description">
                      {option.description}
                    </small>
                  )}
                </div>
              }
            />
          ))}
        </Form.Group>

        {selectedOption && hasChanges && (
          <Alert variant="info" className="mt-3 change-preview">
            <div className="d-flex align-items-center">
              <i className="fas fa-info-circle me-2"></i>
              <div>
                <strong>Preview:</strong> {itemType} will be set to{' '}
                <span className={`badge bg-${selectedOption.variant}`}>
                  {selectedOption.label}
                </span>
              </div>
            </div>
          </Alert>
        )}
      </Modal.Body>
      
      <Modal.Footer className="status-modal-footer">
        <Button variant="outline-secondary" onClick={onHide} disabled={loading}>
          Cancel
        </Button>
        <Button 
          variant={hasChanges ? "primary" : "secondary"}
          onClick={handleConfirm} 
          disabled={loading}
          className="confirm-btn"
        >
          {loading ? (
            <>
              <div className="spinner-border spinner-border-sm me-2" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              Updating...
            </>
          ) : hasChanges ? (
            <>
              <i className="fas fa-check me-2"></i>
              Update Status
            </>
          ) : (
            'No Changes'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default StatusChangeModal; 